import { sleep } from '@m43/core';
import {
    createUseStream,
    SocketListener,
    StreamListener,
    createUseSocketStatus,
} from '@m43/sockets-mark43';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AdditionalInfoFormViewNext,
    AdditionalInfoType,
    AdditionalInfoView,
    Priority,
    StreamMap,
} from '../../types';
import { AdditionalInfoStore } from './additional-info-store';
import { getNarrativeSocketConnectAction } from './get-narrative-socket-connect-action';
import {
    AdditionalInfoNotificationManager,
    ListDirection,
    SortDirection,
    UnreadInfo,
} from './types';
import { useAdditionalInfosResource } from './use-additional-infos-event-resource';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useAdditionalInfosBoloResource } from './use-additional-infos-bolo-resource';
import { autorun, runInAction, toJS } from 'mobx';
import { getOriginallyAppliedFilters } from './helpers';
import { createEventController } from './controllers/create-event-controller';
import { createScheduledEventController } from './controllers/create-scheduled-event-controller';
import { createBoloController } from './controllers/create-bolo-controller';
import { useNarrativeFocusFlash } from '../use-narrative-focus-flash/use-narrative-focus-flash';

const useStream = createUseStream<StreamMap>();

const useSocketStatus = createUseSocketStatus<StreamMap>();

const { DESC } = SortDirection;

/** Delay before clearing unread indicators after infos become visible. */
export const MARK_AS_READ_DELAY_MS = 3000;

const getListDirection = (_sortDirection?: SortDirection): ListDirection => ListDirection.HISTORIC;

// Derives directional cursors from the loaded rows when the BE didn't ship
// them. The legacy endpoint (and some mocks) only returns `hasNextPage`
// without a concrete `nextCursor`; the new endpoint always returns the
// directional pair directly. Doing this on the FE side keeps continuation
// working identically across both endpoints. Shared by fetchNextPage and
// reloadPage so a reload-sourced initial page seeds cursors the same way a
// fetchNextPage-sourced one does — otherwise store.olderCursor stays
// undefined after a reload, and the next fetchNextPage call misreads that as
// "no cursor yet" and resets (wiping the just-loaded page) instead of
// paginating from it.
const deriveDirectionalCursors = (
    pagination: { olderCursor?: string; newerCursor?: string },
    additionalInfos: AdditionalInfoView[]
): { olderCursor?: string; newerCursor?: string } | undefined => {
    if (!additionalInfos.length) return undefined;
    return {
        olderCursor:
            pagination.olderCursor ?? String(additionalInfos[additionalInfos.length - 1].id),
        newerCursor: pagination.newerCursor ?? String(additionalInfos[0].id),
    };
};

/** Result of {@link reanchorAtMessage}, {@link jumpToNewest}, {@link jumpToOldest}.
 *  - `inStore: true`  — target is in the loaded window; `index` is its position
 *    for AdditionalInfoRenderer to scrollToIndex.
 *  - `inStore: false` — target requires an aroundId BE fetch, already kicked off. */
export type ReanchorResult = { inStore: true; index: number } | { inStore: false };

export type UseAdditionalInfosParams = {
    entityId: number;
    entityNumber: string;
    isBolo?: boolean;
    eventOptions?: {
        includeEventNarratives?: boolean;
        isScheduledEvent?: boolean;
    };
    currentUserId: number;
    firstUnreadInfo?: UnreadInfo;
    initialFilter?: Array<AdditionalInfoType>;
    initialSortDirection?: SortDirection;

    onAllRead?: (id: number) => void;
    pageSize?: number;
    notificationManager?: AdditionalInfoNotificationManager;
    crossClassEventsIds?: number[];
    /**
     * Whether this store may show pinned narratives. Opt-in: defaults to false
     * so a store only fetches/reconciles pinned entries when a panel explicitly
     * asks for them. The Narrative panel (Regular Event / Scheduled Event) passes
     * true to enable the bootstrap `POST /narrative/pinned` fetch and the realtime
     * pinned reconcile. Message-source views (ProQA) and BOLO leave it unset, so
     * a pinned narrative can never appear there (RND-64170).
     */
    enablePinnedEntries?: boolean;
};

/**
 * Entity-agnostic orchestration hook for the narrative store (Event / Scheduled
 * Event / BOLO). It owns store wiring, pagination, realtime plumbing, and the
 * public API the UI consumes — NOT entity-specific behavior.
 *
 * !!! ARCHITECTURE INVARIANT — keep this hook entity-agnostic !!!
 * The ONLY entity-specific decision made here is which controller to construct
 * (`createEventController` / `createScheduledEventController` / `createBoloController`)
 * in the `controller` useMemo below. Everything that differs by entity type —
 * endpoints, request/response mapping, pagination rules, stream reconciliation,
 * and the pin/unpin/edit/delete/getPinned wire calls — lives behind the
 * `AdditionalInfosController` interface and MUST be reached via `controller.xxx()`.
 *
 * Do NOT:
 *   - add `isBolo` / `isScheduledEvent` branches outside the controller selection,
 *   - call an entity resource method directly for business logic (e.g.
 *     `eventResource.pinNarrative(...)` / `boloResource.getPinnedNarratives(...)`),
 *   - or import an entity-specific helper/mapper here.
 * Instead, add a method to the `AdditionalInfosController` contract and implement
 * it in each controller. The resource hooks are called here ONLY to inject them
 * into the controllers (Rules of Hooks) — never for direct business calls.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useIntegratedAdditionalInfosStore = ({
    entityId,
    entityNumber,
    currentUserId,
    firstUnreadInfo,
    eventOptions,
    initialFilter,
    initialSortDirection = DESC,
    onAllRead,
    pageSize,
    notificationManager,
    isBolo,
    crossClassEventsIds = [],
    enablePinnedEntries = false,
}: UseAdditionalInfosParams) => {
    const includeEventNarratives = eventOptions?.includeEventNarratives ?? true;
    const isScheduledEvent = eventOptions?.isScheduledEvent ?? false;
    const { rollOutCadNarrativeFilteringAndFocusImprovementsEnabled } = useEnabledFeatures();
    const noSystemMessagesByDefault =
        useEnabledFeatures()?.['filterOutSystemMessagesFromNarrativeByDefault'] ?? true;

    const storeRef = useRef<AdditionalInfoStore>();

    // The manager is a fresh object every render; the store is created once.
    // Route the store's removal callback through a ref so it always reaches
    // the current manager instead of the first render's capture.
    const notificationManagerRef = useRef(notificationManager);
    useEffect(() => {
        notificationManagerRef.current = notificationManager;
    });

    if (storeRef.current === undefined) {
        storeRef.current = new AdditionalInfoStore({
            filter: getOriginallyAppliedFilters(initialFilter, noSystemMessagesByDefault),
            firstUnreadInfo,
            sortDirection: initialSortDirection,
            onAllRead: () => onAllRead?.(entityId),
            // Deleted rows can never be scrolled into view — drop their
            // notifications immediately or the unread indicator would be
            // undismissable.
            onInfosRemoved: (ids) => notificationManagerRef.current?.discardNotifications?.(ids),
        });
    }

    const store = storeRef.current;

    // Resource hooks called unconditionally (Rules of Hooks)
    const eventResource = useAdditionalInfosResource(
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled
    );
    const boloResource = useAdditionalInfosBoloResource();

    // Controller is a plain strategy object, not a hook — safe to select conditionally via useMemo
    const controller = useMemo(() => {
        if (isBolo) {
            return createBoloController({
                entityId,
                entityNumber,
                resource: boloResource,
                pageSize,
                enablePinnedEntries,
            });
        }
        if (isScheduledEvent) {
            return createScheduledEventController({
                entityId,
                resource: eventResource,
                pageSize,
                enablePinnedEntries,
            });
        }
        return createEventController({
            entityId,
            resource: eventResource,
            pageSize,
            includeEventNarratives,
            useNarrativeEndpoints: rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
            enablePinnedEntries,
        });
    }, [
        isBolo,
        isScheduledEvent,
        entityId,
        entityNumber,
        eventResource,
        boloResource,
        pageSize,
        includeEventNarratives,
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
        enablePinnedEntries,
    ]);

    const infosListenerRef = useRef<StreamListener<StreamMap, 'infos'[]>>();
    const boloInfosListenerRef = useRef<StreamListener<StreamMap, 'boloAdditionalInfo'[]>>();

    const loadTokenRef = useRef(0);
    const reanchorInFlightRef = useRef(false);

    const loadPage = useCallback(
        async ({
            cursor,
            aroundId,
            listDirection = getListDirection(store.sortDirection),
            resetBeforeApply = false,
        }: {
            cursor?: string;
            aroundId?: number;
            listDirection?: ListDirection;
            resetBeforeApply?: boolean;
        } = {}) => {
            const requestToken = loadTokenRef.current;
            const data = await controller.loadPage({
                cursor,
                aroundId,
                listDirection,
                filter: toJS(store.filter),
                agencyTypeGlobalAttrIds: store.isAgencyTypeFilterActive
                    ? Array.from(store.agencyTypeGlobalAttrIds)
                    : undefined,
            });
            if (
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
                requestToken !== loadTokenRef.current
            ) {
                return { additionalInfos: [], pagination: {}, agencyEventNumberToIdMap: {} };
            }
            // A reanchor jump defers its window reset to here so the outgoing
            // window stays visible during the aroundId round-trip. Clearing and
            // repopulating in one action swaps the window in a single render — the
            // panel never blanks to the full-view "Loading" spinner, which used to
            // take over the whole narrative view and block interaction on every jump.
            if (resetBeforeApply) {
                runInAction(() => {
                    store.reset();
                    store.upsertInfos(data.additionalInfos);
                    store.upsertAgencyEventNumberToIdMap(data.agencyEventNumberToIdMap);
                });
                return data;
            }
            store.upsertInfos(data.additionalInfos);
            store.upsertAgencyEventNumberToIdMap(data.agencyEventNumberToIdMap);
            return data;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [controller, store, rollOutCadNarrativeFilteringAndFocusImprovementsEnabled]
    );

    const reloadPage = useCallback(async () => {
        store.toggleIsPageReloading();

        if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
            // Legacy: reset clears the list + directional cursors (the new-store
            // equivalent of main's entity-change cursor reset); setPagination
            // restores has*Page/cursors from the translated legacy bootstrap
            // response so the renderer can paginate. No latestKnownId seeding —
            // legacy responses carry no newestEntryId.
            store.reset();
            const legacyData = await loadPage();
            store.setPagination(legacyData.pagination);
            store.toggleIsPageReloading();
            return;
        }

        // store.reset() clears the list, latestKnownId, and aroundId-mode
        // bookkeeping (olderCursor/newerCursor/etc.) while preserving sort,
        // filter, and unreads. Bootstrap response repopulates pagination +
        // latestKnownId. Critical for entity switch — a stale cursor from the
        // prior event would otherwise misroute the renderer's edge triggers.
        store.reset();
        const data = await loadPage();
        store.setPagination(data.pagination);
        const derivedCursors = deriveDirectionalCursors(data.pagination, data.additionalInfos);
        if (derivedCursors) store.setPagination(derivedCursors);
        if (data.pagination.newestEntryId != null) {
            store.setLatestKnownId(data.pagination.newestEntryId);
        }
        store.toggleIsPageReloading();
    }, [store, loadPage, rollOutCadNarrativeFilteringAndFocusImprovementsEnabled]);

    const fetchNextPage = useCallback(
        async ({
            reset = false,
            direction = ListDirection.HISTORIC,
            aroundId,
        }: {
            reset?: boolean;
            direction?: ListDirection;
            aroundId?: number;
        } = {}) => {
            store.setIsFetchingNextPage(true);

            if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
                // Legacy single-cursor pagination (HISTORIC only), adapted onto the
                // directional store: olderCursor is the forward cursor. No aroundId /
                // CATCHUP routing and no latestKnownId seeding in this path.
                const legacyCursor = store.olderCursor;
                const shouldResetLegacy = !legacyCursor || reset;
                if (shouldResetLegacy) store.reset();
                try {
                    const { pagination, additionalInfos } = shouldResetLegacy
                        ? await loadPage()
                        : await loadPage({
                              cursor: legacyCursor,
                              listDirection: ListDirection.HISTORIC,
                          });
                    store.setPagination(pagination);
                    if (additionalInfos.length) {
                        store.setPagination({
                            olderCursor:
                                pagination.olderCursor ??
                                String(additionalInfos[additionalInfos.length - 1].id),
                        });
                    }
                    store.setPageFetchFailed(false);
                } catch (e) {
                    store.setPageFetchFailed(true);
                } finally {
                    store.setIsFetchingNextPage(false);
                }
                return;
            }

            // No direction-side cursor (bootstrap), explicit reset, or aroundId
            // → reset the window and refetch from scratch.
            const cursor =
                direction === ListDirection.CATCHUP ? store.newerCursor : store.olderCursor;
            const isReanchorJump = aroundId !== undefined;
            const shouldReset = reset || isReanchorJump || cursor == null;

            if (isReanchorJump) reanchorInFlightRef.current = true;

            if (shouldReset && !isReanchorJump) store.reset();
            try {
                const { pagination, additionalInfos } = shouldReset
                    ? await loadPage(
                          isReanchorJump ? { aroundId, resetBeforeApply: true } : undefined
                      )
                    : await loadPage({ cursor, listDirection: direction });

                store.setPagination(pagination);

                const derivedCursors = deriveDirectionalCursors(pagination, additionalInfos);
                if (derivedCursors) store.setPagination(derivedCursors);

                // Seed latestKnownId from authoritative bootstrap/aroundId metadata.
                // Bootstrap and aroundId responses include newestEntryId (the event's
                // newest entry id, which may be beyond the loaded window). Continued-
                // scroll pages don't include it — fall back to upsertInfos auto-advance.
                if (pagination.newestEntryId != null) {
                    store.setLatestKnownId(pagination.newestEntryId);
                }

                store.setPageFetchFailed(false);
            } catch (e) {
                // A failed next page load is caught and the pageFetchFailed boolean is set to
                // true. Set to true this boolean stops retrying of the fetch. Setting it to false
                // causes the fetch to try again.
                store.setPageFetchFailed(true);
            } finally {
                store.setIsFetchingNextPage(false);
                if (isReanchorJump) reanchorInFlightRef.current = false;
            }
        },
        [loadPage, store, rollOutCadNarrativeFilteringAndFocusImprovementsEnabled]
    );

    const retryPageFetch = (): void => {
        store.setPageFetchFailed(false);
    };

    const setSortDirection = useCallback(
        (sortDirection: SortDirection) => {
            // Reversing direction is display-only: store.setSortDirection re-sorts
            // the already-loaded rows in place (listStore.recomputeList) and the
            // pagination cursors are chronological, so hasNextPage/hasPrevPage
            // re-derive from the new direction and the same window simply renders
            // reversed. Do NOT reset + refetch — the fetch returns the identical
            // rows while discarding the user's loaded history. Contrast
            // setFilter/toggleAgencyTypeFilter below, which change the server-side
            // result set and must refetch.
            store.setSortDirection(sortDirection);
        },
        [store]
    );

    const setFilter = useCallback(
        (filter: AdditionalInfoType[]) => {
            store.setFilter(filter);
            fetchNextPage({ reset: true });
        },
        [store, fetchNextPage]
    );

    const syncAgencyTypeFilter = useCallback(
        (participatingIds: number[]) => {
            store.syncAgencyTypeFilterForEvent(entityId, participatingIds);
        },
        [store, entityId]
    );

    // Check/uncheck a class, then reset the list — mirrors setFilter (Message
    // Source). fetchNextPage({ reset: true }) re-inits latestKnownId/oldestEntryId
    // via store.reset() + bootstrap response, identical to the Message Source path.
    const toggleAgencyTypeFilter = useCallback(
        (agencyTypeId: number) => {
            store.toggleAgencyTypeFilter(agencyTypeId);
            fetchNextPage({ reset: true });
        },
        [store, fetchNextPage]
    );

    /**
     * Deletes an Additional Info.
     *
     * @param infoId - ID of the Additional Infos to delete.
     * @returns No value returned.
     * @throws An error if the Additional Info delete failed.
     */
    const deleteInfo = useCallback(
        async (infoId: number, agencyEventId?: number) => {
            try {
                await controller.deleteInfo(infoId, agencyEventId);
                store.removeInfos([infoId]);
            } catch (error) {
                throw new Error(`Error while trying to delete Additional Info id=${infoId}`);
            }
        },
        [controller, store]
    );

    const createAdditionalInfo = useCallback(
        async (info: AdditionalInfoFormViewNext): Promise<AdditionalInfoView> => {
            const infosToUpsert = await controller.createInfo(info);
            store.upsertInfos(infosToUpsert);
            return infosToUpsert[0];
        },
        [controller, store]
    );

    // Reconcile both the loaded list copy AND the separate pinned-section copy
    // from a text/priority change WITHOUT reordering the pinned entry — pin order
    // is by pin datetime only. No-op if the entry isn't loaded / isn't pinned. The
    // realtime infos.update nudge reconciles the same entry idempotently afterwards.
    const syncUpdatedInfo = useCallback(
        (partial: { id: number; text: string; priority: string }): void => {
            const existing = store.listStore.getItemById(partial.id);
            if (existing) {
                store.upsertInfos([
                    {
                        ...existing,
                        text: partial.text,
                        priority: partial.priority as AdditionalInfoView['priority'],
                    },
                ]);
            }
            store.updatePinnedEntry({
                id: partial.id,
                text: partial.text,
                priority: partial.priority as AdditionalInfoView['priority'],
            });
        },
        [store]
    );

    const editInfo = useCallback(
        async ({
            additionalInfoId,
            text,
        }: {
            additionalInfoId: number;
            text?: string | null;
        }): Promise<void> => {
            syncUpdatedInfo(await controller.editInfo({ additionalInfoId, text }));
        },
        [controller, syncUpdatedInfo]
    );

    const emphasizeInfo = useCallback(
        async (additionalInfoId: number): Promise<void> => {
            syncUpdatedInfo(
                await controller.setPriority({ additionalInfoId, priority: Priority.HIGH })
            );
        },
        [controller, syncUpdatedInfo]
    );

    const removeEmphasis = useCallback(
        async (additionalInfoId: number): Promise<void> => {
            syncUpdatedInfo(
                await controller.setPriority({ additionalInfoId, priority: Priority.STANDARD })
            );
        },
        [controller, syncUpdatedInfo]
    );

    /**
     * Pin a narrative. The controller hits the entity-specific pin endpoint
     * (event vs BOLO) and returns the re-mapped view with isPinned=true /
     * pinnedDateUtc / pinnedByUserId. We add it to the pinned section AND flip
     * isPinned on the loaded main-list copy through the single upsertInfos path
     * (no bypass). Errors (e.g. the pin-cap HTTP 400) reject to the caller, which
     * reads the cap from the error body and surfaces a toast.
     */
    const pinNarrative = useCallback(
        async (additionalInfoId: number, agencyEventId?: number): Promise<void> => {
            const view = await controller.pinNarrative(additionalInfoId, agencyEventId);
            store.addPinnedEntry(view);
            store.upsertInfos([view]);
        },
        [controller, store]
    );

    const unpinNarrative = useCallback(
        async (additionalInfoId: number, agencyEventId?: number): Promise<void> => {
            const view = await controller.unpinNarrative(additionalInfoId, agencyEventId);
            store.removePinnedEntry(additionalInfoId);
            store.upsertInfos([view]);
        },
        [controller, store]
    );

    // Start of infinite scroll logic

    // After reanchorAtMessage triggers an aroundId BE fetch, AdditionalInfoRenderer
    // watches this id and calls scrollToIndex once the row appears in the loaded
    // window. The renderer clears it via clearPendingScrollTarget after scrolling.
    const [pendingScrollTargetId, setPendingScrollTargetId] = useState<number | undefined>(
        undefined
    );
    const clearPendingScrollTarget = useCallback(() => {
        setPendingScrollTargetId(undefined);
    }, []);

    const { focusedFlashId, flashMessage } = useNarrativeFocusFlash();

    /** Scroll/load so that targetId is visible. Returns { inStore: true, index } if
     *  the target is already in the loaded window — the caller (AdditionalInfoRenderer)
     *  does the scrollToIndex via the virtualizer it owns. Otherwise triggers an
     *  aroundId reset and returns { inStore: false }; pendingScrollTargetId is set
     *  so AdditionalInfoRenderer scrolls once the fetch completes. */
    const reanchorAtMessage = useCallback(
        (targetId: number): ReanchorResult => {
            // ListStore.getItemIndex returns number | void (void when not found).
            const index = store.listStore.getItemIndex(targetId);
            if (index !== undefined && index >= 0) {
                return { inStore: true, index };
            }
            // Ignore a new reanchor while one is still loading its window — the
            // outgoing window is still on screen and clickable, so without this a
            // second jump would race a competing window swap.
            if (reanchorInFlightRef.current) {
                return { inStore: false };
            }
            setPendingScrollTargetId(targetId);
            fetchNextPage({ aroundId: targetId });
            return { inStore: false };
        },
        [store, fetchNextPage]
    );

    const jumpToNewest = useCallback((): ReanchorResult => {
        const targetId = store.latestKnownId;
        if (targetId === undefined) {
            return { inStore: false };
        }
        return reanchorAtMessage(targetId);
    }, [store, reanchorAtMessage]);

    const jumpToOldest = useCallback((): ReanchorResult => {
        const targetId = store.data.pagination.oldestEntryId;
        if (targetId === undefined) {
            return { inStore: false };
        }
        return reanchorAtMessage(targetId);
    }, [store, reanchorAtMessage]);

    // End of infinite scroll logic

    // Start Realtime Update logic

    const reloadPinnedNarratives = useCallback(async () => {
        if (
            !rollOutCadNarrativeFilteringAndFocusImprovementsEnabled ||
            !enablePinnedEntries ||
            !entityId
        ) {
            return;
        }
        try {
            const pinnedNarratives = await controller.getPinnedNarratives();
            store.setPinnedEntries(pinnedNarratives);
        } catch {
            // Uses shouldFailSilently; leave the existing section in place on a
            // failed reload rather than surfacing an unhandled rejection.
        }
    }, [
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
        enablePinnedEntries,
        entityId,
        controller,
        store,
    ]);

    const updateStreamListeners = useCallback(() => {
        const listeners = controller.createStreamListeners({
            store,
            currentUserId,
            fetchNextPage,
            reloadPinnedNarratives,
            getLoadToken: () => loadTokenRef.current,
        });
        infosListenerRef.current = listeners.infos;
        boloInfosListenerRef.current = listeners.boloAdditionalInfo;
    }, [controller, store, currentUserId, fetchNextPage, reloadPinnedNarratives]);

    useEffect(
        function reinitializeOnEntityChange() {
            loadTokenRef.current += 1;
            reanchorInFlightRef.current = false;
            store.clearActiveRowAction();
            store.clearSelectedRow();
            reloadPage();
            updateStreamListeners();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    useEffect(
        function updateFilterDirective() {
            return autorun(() => {
                store.listStore.setFilterDirective(
                    controller.createFilterDirective(crossClassEventsIds)
                );
            });
        },
        [store, entityId, crossClassEventsIds, controller]
    );

    useEffect(
        function loadPinnedNarratives() {
            if (
                !rollOutCadNarrativeFilteringAndFocusImprovementsEnabled ||
                // Message-source views (ProQA) opt out of the pinned section.
                !enablePinnedEntries ||
                !entityId
            ) {
                return;
            }
            // Ignore a stale response if the user switches entities before the
            // fetch resolves — otherwise a slow response for the previous entity
            // could overwrite the pinned section of the entity now in view.
            let isCancelled = false;
            (async function fetchPinnedNarratives() {
                try {
                    const pinnedNarratives = await controller.getPinnedNarratives();
                    if (isCancelled) {
                        return;
                    }
                    store.setPinnedEntries(pinnedNarratives);
                } catch {
                    // The request uses shouldFailSilently; swallow the rejection so a
                    // failed bootstrap of the pinned section (e.g. offline) doesn't
                    // surface an unhandled rejection. The section simply stays empty.
                }
            })();
            return () => {
                isCancelled = true;
                store.setPinnedEntries([]);
            };
        },
        [
            rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
            enablePinnedEntries,
            entityId,
            controller,
            store,
        ]
    );

    if (infosListenerRef.current === undefined) {
        updateStreamListeners();
    }

    useStream(infosListenerRef.current);

    useStream(boloInfosListenerRef.current);

    const socketListenerRef = useRef<SocketListener<StreamMap>>();

    if (socketListenerRef.current === undefined) {
        socketListenerRef.current = {
            invokeOnInitialize: true,
            onSocketStatusChange: ({ status }) => {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
                    if (status === 'CONNECTED') {
                        if (!store.initialSocketConnectionMade) {
                            store.setInitialSocketConnectionMade(true);
                        }
                        fetchNextPage({ reset: true });
                    }
                    return;
                }
                // Flag on: the initial connect's bootstrap is owned by
                // reinitializeOnEntityChange, so refetching here would duplicate it.
                // Skip the first connect; only reconnects refetch.
                const action = getNarrativeSocketConnectAction(
                    status,
                    store.initialSocketConnectionMade
                );
                if (action === 'ignore') {
                    return;
                }
                if (action === 'mark-initial') {
                    store.setInitialSocketConnectionMade(true);
                    return;
                }
                // 'refetch' — genuine reconnect, catch up on missed messages.
                fetchNextPage({ reset: true });
            },
        };
    }

    useSocketStatus(socketListenerRef.current);
    // End of Realtime Update logic

    // Start of Unread Infos Logic
    const markAsRead = async (infos: AdditionalInfoView[], immediate = false): Promise<void> => {
        if (!immediate) {
            await sleep(MARK_AS_READ_DELAY_MS);
        }
        store.clearUnreadsBasedOnOldestUnread(infos);
    };

    const managerToUse: AdditionalInfoNotificationManager = notificationManager ?? {
        hasUnread: store.hasUnread,
        unreadInfos: store.unreadInfos,
        earliestUnreadInfo: store.earliestUnreadInfo,
        markAsRead,
        syncUnreadPriority: store.syncUnreadPriority,
    };

    store.onSyncUnreadPriority = managerToUse.syncUnreadPriority;

    return {
        ...managerToUse,
        entityId,
        entityNumber,
        agencyEventNumberToIdMap: store.agencyEventNumberToIdMap,
        createAdditionalInfo,
        deleteInfo,
        editInfo,
        activeRowAction: store.activeRowAction,
        editorState: store.editorState,
        emphasizeInfo,
        fetchNextPage,
        filter: store.filter,
        getIndexById: store.listStore.getItemIndex.bind(store.listStore),
        // Sort-aware edge gating. DESC: visual bottom = older, visual top = newer.
        // ASC: visual bottom = newer, visual top = older. The renderer gates its
        // bottom-edge / top-edge fetches on these.
        //
        // In normal scroll mode (no aroundId), whichever edge maps to the
        // "newer" direction stays dead by construction — HISTORIC fetches
        // never populate hasNewerPage/newerCursor. That's DESC top-edge and
        // ASC bottom-edge: both naturally no-ops, both correct (we've already
        // loaded up to the latest, so there's nothing newer to fetch).
        //
        // The other two edges drive normal-scroll continuation: DESC bottom
        // (HISTORIC) is the legacy infinite-scroll path; ASC top (HISTORIC) is
        // the symmetric "scroll up to load older" path.
        //
        // aroundId mode populates both directional pairs from the around
        // response, so all four edge × sort combinations work.
        // Flag-off: the legacy path only ever fetches HISTORIC (getListDirection
        // is always HISTORIC) via a single bottom-edge trigger, so "more pages"
        // is hasOlderPage regardless of sort — matching main's sort-agnostic
        // store.hasNextPage. There is no top-edge/upward fetch in legacy mode, so
        // hasPrevPage is always false. Gating this is required: without it, ASC +
        // flag-off would read hasNewerPage (never populated by HISTORIC) and
        // pagination would stop after the first page.
        hasNextPage: !rollOutCadNarrativeFilteringAndFocusImprovementsEnabled
            ? store.hasOlderPage
            : store.sortDirection === SortDirection.DESC
              ? store.hasOlderPage
              : store.hasNewerPage,
        hasPrevPage: !rollOutCadNarrativeFilteringAndFocusImprovementsEnabled
            ? false
            : store.sortDirection === SortDirection.DESC
              ? store.hasNewerPage
              : store.hasOlderPage,
        hiddenPinnedCount: store.hiddenPinnedCount,
        initialSocketConnectionMade: store.initialSocketConnectionMade,
        isFetchingNextPage: store.isFetchingNextPage,
        isHighPriority: store.isHighPriority,
        isPageReloading: store.isPageReloading,
        isPinnedSectionExpanded: store.isPinnedSectionExpanded,
        jumpToOldest,
        jumpToNewest,
        latestKnownId: store.latestKnownId,
        list: store.listStore.data.list,
        markAsUnread: store.markAsUnread,
        oldestEntryId: store.data.pagination.oldestEntryId,
        pageFetchFailed: store.pageFetchFailed,
        pageSize,
        pagination: store.data.pagination,
        pendingScrollTargetId,
        clearPendingScrollTarget,
        focusedFlashId,
        flashMessage,
        pinNarrative,
        unpinNarrative,
        reanchorAtMessage,
        reloadPage,
        removeEmphasis,
        retryPageFetch,
        setActiveRowAction: store.setActiveRowAction,
        clearActiveRowAction: store.clearActiveRowAction,
        selectedRow: store.selectedRow,
        setSelectedRow: store.setSelectedRow,
        clearSelectedRow: store.clearSelectedRow,
        clearSelectedRowIfMatches: store.clearSelectedRowIfMatches,
        setFilter,
        agencyTypeGlobalAttrIds: store.agencyTypeGlobalAttrIds,
        isAgencyTypeFilterActive: store.isAgencyTypeFilterActive,
        syncAgencyTypeFilter,
        toggleAgencyTypeFilter,
        setIsHighPriority: store.setIsHighPriority,
        setSortDirection,
        setPinnedEntries: store.setPinnedEntries,
        showPinnedDivider: store.showPinnedDivider,
        sortDirection: store.sortDirection,
        storeEditorState: store.storeEditorState,
        togglePinnedSection: store.togglePinnedSection,
        visiblePinnedEntries: store.visiblePinnedEntries,
        pinnedEntries: store.pinnedEntries,
    };
};

// AdditionalInfosContext, useAdditionalInfos, and AdditionalInfosStoreProvider
// are in ./additional-infos-provider.tsx — import directly from there to avoid
// circular dependencies.
